import {addDays} from 'date-fns/addDays'
import {eachDayOfInterval} from 'date-fns/eachDayOfInterval'
import {isBefore} from 'date-fns/isBefore'
import {isSameDay} from 'date-fns/isSameDay'
import {compose, map, reduce} from 'ramda'

import {
  dateFormat,
  dateStringToMiddayDate,
  dateToMiddayDate,
  UTS_DATE_FORMAT
} from '../utils/date'
import {DatePickerState} from './types'

/**
 * Returns the earliest valid check-in date adjusted to the midday local time (today at 12:00)
 * @returns The earliest valid check-in date
 */
export const getEarliestCheckInDate = (): Date => dateToMiddayDate(new Date())

const earliestCheckInDate = getEarliestCheckInDate()
const earliestCheckOutDate = addDays(earliestCheckInDate, 1)

export const formatUTS = (date: Date) => dateFormat(date, UTS_DATE_FORMAT)

const daysReducer = (
  hasDatesRange: boolean,
  startDate: string,
  endDate?: string
) =>
  reduce((config: {[key: string]: object}, day: string) => {
    config[day] = {
      selected: hasDatesRange,
      startingDay: day === startDate,
      endingDay: day === endDate
    }
    return config
  }, {})

/**
 * Gets marked days configuration for the date picker calendar.
 * @param startDate - The start date string
 * @param endDate - The optional end date string
 * @returns Configuration object for marked days
 */
export const getMarkedDays = (startDate: string, endDate?: string) => {
  const hasDatesRange = !!startDate && !!endDate
  const markedDaysReducer = daysReducer(hasDatesRange, startDate, endDate)

  if (hasDatesRange) {
    if (
      isBefore(
        dateStringToMiddayDate(endDate),
        dateStringToMiddayDate(startDate)
      )
    ) {
      throw new Error('Invalid interval')
    }
    return compose(
      markedDaysReducer,
      map(formatUTS),
      eachDayOfInterval
    )({
      start: dateStringToMiddayDate(startDate),
      end: dateStringToMiddayDate(endDate as string)
    })
  } else {
    return markedDaysReducer([startDate])
  }
}

/**
 * Gets the earliest check-in date as a formatted string.
 * @returns The earliest check-in date in UTS format
 */
export const getEarliestCheckInDateString = () =>
  dateFormat(earliestCheckInDate, UTS_DATE_FORMAT)
/**
 * Gets the earliest check-out date as a formatted string.
 * @returns The earliest check-out date in UTS format
 */
export const getEarliestCheckOutDateString = () =>
  dateFormat(earliestCheckOutDate, UTS_DATE_FORMAT)

/**
 * Gets the maximum selectable date based on check-in date and maximum length of stay.
 * @param checkInDateString - The check-in date string
 * @param maxLengthOfStay - Maximum number of days for the stay
 * @returns The maximum selectable date in UTS format
 */
export const getMaxSelectableDate = (
  checkInDateString: string,
  maxLengthOfStay: number
) => {
  const checkInDate = dateStringToMiddayDate(checkInDateString)
  return compose(formatUTS, addDays)(checkInDate, maxLengthOfStay)
}
/**
 * Gets the check-out date string from a check-in date (next day).
 * @param checkInDate - The check-in date
 * @returns The check-out date string in UTS format
 */
export const getCheckOutDateStringFromCheckIn = (checkInDate: Date) => {
  const nextCheckOutDate = addDays(checkInDate, 1)
  return formatUTS(nextCheckOutDate)
}

/**
 * Checks if the selected date is the same or before the date to compare.
 * @param selectedDate - The selected date string
 * @param dateToCompare - The date string to compare against
 * @returns True if selected date is same or before the comparison date
 */
export const isSameOrBeforeDate = (
  selectedDate: string,
  dateToCompare: string
) => {
  const selectedDay = dateStringToMiddayDate(selectedDate)
  const comparableDay = dateStringToMiddayDate(dateToCompare)
  const selectedPastStartDate = isBefore(selectedDay, comparableDay)
  const selectedStartDateAgain = isSameDay(selectedDay, comparableDay)
  return selectedPastStartDate || selectedStartDateAgain
}

/**
 * Gets the next date picker state based on check-in and optional check-out dates.
 * @param checkIn - The check-in date
 * @param checkOut - The optional check-out date
 * @returns The next date picker state with formatted dates
 */
export const getNextDatePickerState: (
  checkIn: Date,
  checkOut?: Date | null
) => DatePickerState = (checkIn, checkOut) => {
  const nextCheckOut = checkOut ?? addDays(checkIn, 1)
  return {
    checkIn: formatUTS(checkIn),
    checkOut: formatUTS(nextCheckOut)
  }
}
